import { FieldGroupWrapper } from '~/app/pages/HospitalConfiguration/Form/FormWrapper'
import { createFormFieldMetaProps } from '~/app/pages/HospitalConfiguration/Form/utils'
import { AstraButton, AstraCallout, AstraTextArea, AstraTextField } from '~/astra'

import { SupportView } from '../UserSupportDialog'
import { AutoSubmittedInfo } from './AutoSubmittedInfo'
import { formByView } from './utils/formTexts'
import { useSupportFeedbackForm } from './utils/useSupportFeedbackForm'

type Props = {
    activeView: Exclude<SupportView, 'menu'>
    onClose?: () => void
}

export const UserFeedbackForm = ({ activeView, onClose }: Props) => {
    const { form, metadata, showContactSaveCallout } = useSupportFeedbackForm(activeView, onClose)

    return (
        <form
            onSubmit={e => {
                e.preventDefault()
                e.stopPropagation()
                void form.handleSubmit()
            }}
            className="flex flex-col gap-6"
        >
            <form.AppForm>
                <form.FormErrorCallout />
                <div className="flex flex-col gap-6 sm:flex-row">
                    <div className="flex flex-1 flex-col gap-6">
                        <FieldGroupWrapper className="grid-cols-1 sm:grid-cols-1">
                            <form.Field name="subject">
                                {field => {
                                    const meta = createFormFieldMetaProps(field)
                                    return (
                                        <AstraTextField
                                            label={formByView[activeView].labels.subject}
                                            value={field.state.value}
                                            onChange={e => field.handleChange(e.target.value)}
                                            onBlur={field.handleBlur}
                                            error={meta.error}
                                            className="w-72"
                                            helperText={formByView[activeView].hints.subject}
                                            errorText={meta.errorText}
                                        />
                                    )
                                }}
                            </form.Field>
                            <form.Field name="description">
                                {field => {
                                    const meta = createFormFieldMetaProps(field)
                                    return (
                                        <AstraTextArea
                                            label={formByView[activeView].labels.description}
                                            defaultValue={field.state.value}
                                            onChange={e => field.handleChange(e.target.value)}
                                            onBlur={field.handleBlur}
                                            rows={5}
                                            fullWidth
                                            helperText={formByView[activeView].hints.description}
                                            errorText={meta.errorText}
                                            error={meta.error}
                                        />
                                    )
                                }}
                            </form.Field>
                        </FieldGroupWrapper>

                        <FieldGroupWrapper className="grid-cols-1 sm:grid-cols-1">
                            <form.Field name="email">
                                {field => {
                                    const meta = createFormFieldMetaProps(field)
                                    return (
                                        <AstraTextField
                                            label="E-post"
                                            className="w-72"
                                            type="email"
                                            value={field.state.value}
                                            onChange={e => field.handleChange(e.target.value)}
                                            onBlur={field.handleBlur}
                                            error={meta.error}
                                            helperText={formByView[activeView].hints.email}
                                            errorText={meta.errorText}
                                            fullWidth
                                        />
                                    )
                                }}
                            </form.Field>
                            {showContactSaveCallout && (
                                <AstraCallout
                                    color="info"
                                    text="Vi lagrer e-post og telefonnummer, slik at du slipper å skrive det inn neste gang."
                                    data-test="contact-save-callout"
                                />
                            )}

                            <form.Field name="phone">
                                {field => {
                                    const meta = createFormFieldMetaProps(field)
                                    return (
                                        <AstraTextField
                                            label="Telefon (valgfritt)"
                                            className="w-72"
                                            type="tel"
                                            value={field.state.value}
                                            onChange={e => field.handleChange(e.target.value)}
                                            onBlur={field.handleBlur}
                                            error={meta.error}
                                            helperText={formByView[activeView].hints.phone}
                                            errorText={meta.errorText}
                                            fullWidth
                                        />
                                    )
                                }}
                            </form.Field>
                        </FieldGroupWrapper>
                    </div>
                    <AutoSubmittedInfo {...metadata} />
                </div>
            </form.AppForm>

            <div className="flex justify-end gap-2 border-t border-base pt-4">
                <AstraButton type="button" variant="secondary" label="Avbryt" onClick={onClose} />
                <form.Subscribe selector={state => [state.canSubmit, state.isSubmitting] as const}>
                    {([canSubmit, isSubmitting]) => <AstraButton type="submit" label="Send" disabled={!canSubmit} isLoading={isSubmitting} />}
                </form.Subscribe>
            </div>
        </form>
    )
}
